import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import type { EditCandidate } from '@/contexts/EditModeContext';
import { useEditMode } from '@/contexts/EditModeContext';
import { apiClient } from '@/lib/api/client';
import i18n from '@/i18n';
import { HStack } from '@/components/core';
import { cacheDraftOverride } from '@/lib/i18n-draft-cache';

// Custom event for notifying AdminFloatingButton about draft changes
export const DRAFT_CHANGED_EVENT = 'i18n-draft-changed';

export const GlobalTextEditor: React.FC = () => {
  const { editTarget, setEditTarget } = useEditMode();
  const [draftValue, setDraftValue] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<EditCandidate | null>(null);
  const [saving, setSaving] = useState(false);

  const isOpen = !!editTarget;

  useEffect(() => {
    if (editTarget && editTarget.candidates.length > 0) {
      const first = editTarget.candidates[0];
      setSelectedCandidate(first);

      const current = i18n.t(`${first.namespace}:${first.key}`);
      setDraftValue(typeof current === 'string' ? current : first.currentValue);
    }
  }, [editTarget]);

  useEffect(() => {
    if (selectedCandidate) {
      const current = i18n.t(`${selectedCandidate.namespace}:${selectedCandidate.key}`);
      setDraftValue(typeof current === 'string' ? current : selectedCandidate.currentValue);
    }
  }, [selectedCandidate]);

  const handleClose = () => {
    setEditTarget(null);
    setDraftValue('');
    setSelectedCandidate(null);
  };

  const handleSave = async () => {
    if (!selectedCandidate) return;
    setSaving(true);
    try {
      const { namespace, key } = selectedCandidate;
      const lang = i18n.language || 'de';

      // 1. Check for existing draft
      const res = await apiClient.get('/admin/i18n/keys', {
        params: { namespace, key, locale: lang, status: 'draft' }
      });
      const first = Array.isArray(res.data) ? res.data[0] : null;

      if (first) {
        await apiClient.put(`/admin/i18n/keys/${first.id}`, {
          value: draftValue
        });
      } else {
        await apiClient.post('/admin/i18n/keys', {
          namespace, key, locale: lang, value: draftValue
        });
      }

      // 2. Optimistic update + LocalStorage cache for instant preview
      try {
        const patch: Record<string, any> = {};
        let parsed: any = draftValue;
        if (draftValue.trim().startsWith('{') || draftValue.trim().startsWith('[')) {
          try { parsed = JSON.parse(draftValue); } catch {}
        }

        // Cache in LocalStorage so it survives reloads (15 min TTL)
        cacheDraftOverride(namespace, key, lang, parsed);

        const segments = key.split('.');
        let cursor: any = patch;
        for (let i = 0; i < segments.length; i++) {
          const seg = segments[i]!;
          if (i === segments.length - 1) {
            cursor[seg] = parsed;
          } else {
            cursor = cursor[seg] = cursor[seg] || {};
          }
        }
        i18n.addResourceBundle(lang, namespace, patch, true, true);
        // Force re-render of all useTranslation hooks
        i18n.emit('languageChanged', lang);
      } catch {}

      // 3. Notify AdminFloatingButton to refresh draft count
      window.dispatchEvent(new CustomEvent(DRAFT_CHANGED_EVENT));

      handleClose();
    } catch (e) {
      console.error('Failed to save translation', e);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !editTarget || !selectedCandidate) return null;

  const style: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10060,
    backgroundColor: 'var(--background)',
  };

  if (editTarget.position) {
    const width = 320;
    const height = editTarget.candidates.length > 1 ? 280 : 220;
    let top = editTarget.position.y + 20;
    let left = editTarget.position.x;

    if (left + width > window.innerWidth) left = window.innerWidth - width - 10;
    if (top + height > window.innerHeight) top = editTarget.position.y - height - 10;

    style.top = Math.max(10, top);
    style.left = Math.max(10, left);
  } else {
    style.top = '50%';
    style.left = '50%';
    style.transform = 'translate(-50%, -50%)';
  }

  return ReactDOM.createPortal(
    <div
        className="fixed inset-0 z-[10050]"
        role="dialog"
        aria-modal="true"
    >
        <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px]" onClick={handleClose} />

        <div
            className="w-[320px] rounded-token-button border border-white/10 bg-muted p-token-md shadow-token-modal animate-in fade-in zoom-in-95 duration-100"
            style={style}
            onClick={(e) => e.stopPropagation()}
        >
            <HStack justify="between" align="start" className="mb-token-sm">
                <div className="flex-1 pr-token-sm min-w-0">
                   {editTarget.candidates.length > 1 ? (
                     <select
                       className="w-full bg-transparent text-xs font-mono border-none p-0 focus:ring-0 cursor-pointer text-white truncate"
                       value={`${selectedCandidate.namespace}:${selectedCandidate.key}`}
                       onChange={(e) => {
                         const [ns, k] = e.target.value.split(':');
                         const cand = editTarget.candidates.find(c => c.namespace === ns && c.key === k);
                         if (cand) setSelectedCandidate(cand);
                       }}
                     >
                       {editTarget.candidates.map(c => (
                         <option key={`${c.namespace}:${c.key}`} value={`${c.namespace}:${c.key}`} className="bg-muted">
                           {c.namespace}:{c.key}
                         </option>
                       ))}
                     </select>
                   ) : (
                     <div className="text-xs font-mono text-muted-foreground break-all">
                        <span className="text-brand-secondary-light">{selectedCandidate.namespace}</span>
                        <span className="mx-0.5">:</span>
                        <span className="text-white">{selectedCandidate.key}</span>
                     </div>
                   )}
                </div>
                <button onClick={handleClose} className="text-muted-foreground hover:text-white shrink-0">✕</button>
            </HStack>

            <textarea
                className="w-full h-32 rounded-token-sm glass-effect p-token-sm text-sm focus:outline-none focus:border-info transition-colors resize-y"
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                placeholder="Übersetzung eingeben..."
                autoFocus
            />

            <HStack justify="end" gap="sm" className="mt-token-sm">
                <button
                    type="button"
                    className="px-3 py-1.5 text-xs rounded-token-sm border border-white/10 hover:bg-white/5 transition-colors"
                    onClick={handleClose}
                >
                    Abbrechen
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium rounded-token-sm bg-info hover:bg-info/90 text-white transition-colors disabled:opacity-50"
                    onClick={() => void handleSave()}
                    disabled={saving}
                >
                    {saving ? 'Speichern...' : 'Speichern'}
                </button>
            </HStack>
        </div>
    </div>,
    document.body
  );
};
